import axios, { AxiosInstance } from 'axios';
import { 
  Animal, 
  Ranch, 
  ImportLog, 
  AnimalLifecycle,
  CreateAnimalForm,
  UpdateAnimalForm,
  AddLifecycleStageForm,
  CreateRanchForm,
  BulkUpdateForm,
  AnimalFilters,
  RanchFilters,
  PaginationParams,
  PaginatedResponse,
  ApiResponse
} from '../types';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
          if (token) {
            console.log('Adding auth token to request');
            config.headers.Authorization = `Bearer ${token}`;
          } else {
          console.log('No auth token available for request');
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        console.error('API error:', error.response?.status, error.response?.data);
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login
          this.handleUnauthorized();
        }
        return Promise.reject(error.response?.data || error);
      }
    );
  }

  private getAuthToken(): string | null {
    const token = localStorage.getItem('authToken');
    return token;
  }

  private handleUnauthorized(): void {
    // Clear auth data and redirect to login
    console.log('Unauthorized request detected, redirecting to login');
    localStorage.removeItem('authToken');
    window.location.href = '/login';
  }

  // Set auth token (called by auth context)
  setAuthToken(token: string): void {
    console.log('Setting auth token in localStorage', { tokenLength: token.length });
    localStorage.setItem('authToken', token);
  }

  // Clear auth token
  clearAuthToken(): void {
    console.log('Clearing auth token from localStorage');
    localStorage.removeItem('authToken');
  }

  // Animals API
  async getAnimals(
    filters: AnimalFilters = {}, 
    pagination: PaginationParams = {}
  ): Promise<PaginatedResponse<Animal>> {
    const params = { ...filters, ...pagination };
    return this.client.get('/animals', { params });
  }

  async getAnimalByDin(din: string): Promise<ApiResponse<Animal>> {
    return this.client.get(`/animals/${encodeURIComponent(din)}`);
  }

  async createAnimal(data: CreateAnimalForm): Promise<ApiResponse<Animal>> {
    return this.client.post('/animals', data);
  }

  async updateAnimal(id: string, data: UpdateAnimalForm): Promise<ApiResponse<Animal>> {
    return this.client.put(`/animals/${id}`, data);
  }

  async deleteAnimal(id: string): Promise<ApiResponse> {
    return this.client.delete(`/animals/${id}`);
  }

  async getAnimalLifecycle(din: string): Promise<ApiResponse<{
    animal: Animal;
    lifecycle: AnimalLifecycle[];
  }>> {
    return this.client.get(`/animals/${encodeURIComponent(din)}/lifecycle`);
  }

  async addLifecycleStage(
    din: string,
    data: AddLifecycleStageForm
  ): Promise<ApiResponse<AnimalLifecycle>> {
    return this.client.post(`/animals/${encodeURIComponent(din)}/lifecycle`, data);
  }

  async updateLifecycleStage(
    din: string,
    stageId: string,
    data: Partial<AddLifecycleStageForm>
  ): Promise<ApiResponse<AnimalLifecycle>> {
    return this.client.put(`/animals/${encodeURIComponent(din)}/lifecycle/${stageId}`, data);
  }

  async deleteLifecycleStage(
    din: string,
    stageId: string
  ): Promise<ApiResponse> {
    return this.client.delete(`/animals/${encodeURIComponent(din)}/lifecycle/${stageId}`);
  }

  async bulkUpdateAnimals(data: BulkUpdateForm): Promise<ApiResponse<{
    successful: string[];
    failed: Array<{ din: string; error: string }>;
  }>> {
    return this.client.post('/animals/bulk-update', data);
  }

  // Ranches API
  async getRanches(
    filters: RanchFilters = {}, 
    pagination: PaginationParams = {}
  ): Promise<PaginatedResponse<Ranch>> {
    const params = { ...filters, ...pagination };
    return this.client.get('/ranches', { params });
  }

  async getAllRanches(): Promise<ApiResponse<Ranch[]>> {
    return this.client.get('/ranches/all');
  }

  async getRanchById(id: string): Promise<ApiResponse<Ranch>> {
    return this.client.get(`/ranches/${id}`);
  }

  async createRanch(data: CreateRanchForm): Promise<ApiResponse<Ranch>> {
    return this.client.post('/ranches', data);
  }

  async updateRanch(id: string, data: Partial<CreateRanchForm>): Promise<ApiResponse<Ranch>> {
    return this.client.put(`/ranches/${id}`, data);
  }

  async deleteRanch(id: string): Promise<ApiResponse> {
    return this.client.delete(`/ranches/${id}`);
  }

  async getRanchStatistics(id: string): Promise<ApiResponse<{
    ranch: Ranch;
    statistics: any;
  }>> {
    return this.client.get(`/ranches/${id}/statistics`);
  }

  // Lifecycle API
  async getAllLifecycleStages(pagination: PaginationParams = {}): Promise<PaginatedResponse<any>> {
    const params = { ...pagination };
    return this.client.get('/lifecycle', { params });
  }

  // Certification API
  async retrieveCattleInformation(dins: string[]): Promise<any> {
    return this.client.post('/cattles/certification', { dins });
  }

  async validateCertificationEligibility(din: string): Promise<{
    eligible: boolean;
    reasons?: string[];
  }> {
    return this.client.get(`/certification/validate/${encodeURIComponent(din)}`);
  }

  // Imports API
  async getImportHistory(pagination: PaginationParams = {}): Promise<PaginatedResponse<ImportLog>> {
    return this.client.get('/imports', { params: pagination });
  }

  async getImportById(id: string): Promise<ApiResponse<ImportLog>> {
    if (!id) {
      throw new Error('ID de importación inválido');
    }
    return this.client.get(`/imports/${id}`);
  }

  async uploadFile(file: File, sheetName?: string): Promise<ApiResponse<{
    import_id: string;
    status: string;
  }>> {
    const formData = new FormData();
    formData.append('file', file);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }

    return this.client.post('/imports/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async downloadTemplate(): Promise<Blob> {
    // Bypass the response interceptor for blob responses
    const axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add auth token
    const token = this.getAuthToken();
    if (token) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
    
    const response = await axiosInstance.get('/imports/template', {
      responseType: 'blob',
    });
    
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<any> {
    return this.client.get('/health');
  }

  // ===== USER MANAGEMENT API =====
  
  // Get all users (admin only)
  getUsers = async (): Promise<any[]> => {
    const response = await this.client.get('/admin/users');
    return response as unknown as any[]; // El interceptor ya devuelve response.data
  };

  // Create new user (admin only) 
  createUser = async (userData: {
    email: string;
    password: string;
    displayName: string;
    role: 'data_manager' | 'slaughterhouse_user';
  }): Promise<any> => {
    const response = await this.client.post('/admin/users', userData);
    return response; // El interceptor ya devuelve response.data
  };

  // Update user (admin only)
  updateUser = async (uid: string, userData: {
    displayName?: string;
    role?: 'data_manager' | 'slaughterhouse_user';
  }): Promise<void> => {
    await this.client.put(`/admin/users/${uid}`, userData);
  };

  // Update user role (admin only)
  updateUserRole = async (uid: string, role: 'data_manager' | 'slaughterhouse_user'): Promise<void> => {
    await this.client.put(`/admin/users/${uid}/role`, { role });
  };

  // Toggle user status (admin only)
  toggleUserStatus = async (uid: string, disabled: boolean): Promise<void> => {
    await this.client.put(`/admin/users/${uid}/status`, { disabled });
  };

  // Delete user (admin only)
  deleteUser = async (uid: string): Promise<void> => {
    await this.client.delete(`/admin/users/${uid}`);
  };

  // ===== API KEY MANAGEMENT =====
  
  // Get API keys with pagination
  getApiKeys = async (page: number = 1, limit: number = 10): Promise<any> => {
    const response = await this.client.get(`/admin/api-keys?page=${page}&limit=${limit}`);
    return response;
  };

  // Get API key permissions
  getApiKeyPermissions = async (): Promise<any> => {
    const response = await this.client.get('/admin/api-keys/permissions');
    return response;
  };

  // Get API key statistics
  getApiKeyStatistics = async (): Promise<any> => {
    const response = await this.client.get('/admin/api-keys/statistics');
    return response;
  };

  // Create new API key
  createApiKey = async (data: {
    key_name: string;
    description?: string;
    permissions: string[];
    expires_at?: string;
  }): Promise<any> => {
    const response = await this.client.post('/admin/api-keys', data);
    return response;
  };

  // Update API key
  updateApiKey = async (id: number, data: {
    key_name?: string;
    description?: string;
    permissions?: string[];
    expires_at?: string;
  }): Promise<any> => {
    const response = await this.client.put(`/admin/api-keys/${id}`, data);
    return response;
  };

  // Delete API key
  deleteApiKey = async (id: number): Promise<void> => {
    await this.client.delete(`/admin/api-keys/${id}`);
  };

  // ===== DASHBOARD OPTIMIZED QUERIES =====
  
  // Get stage paths data for "Rutas de Etapas por Ubicación" with pagination
  getStagePathsData = async (filters: any = {}): Promise<any> => {
    const params = new URLSearchParams();
    
    // Add all parameters (filters + pagination)
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await this.client.get(`/dashboard/stage-paths?${params.toString()}`);
    return response; // Backend returns { success: true, data: [...], pagination: {...} }, interceptor returns response.data
  };
}

// Create and export a singleton instance
export const apiService = new ApiService();
// Note: The authTokenProvider will be set by AuthProvider
export default apiService;